import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './styles/Canvas.css';
import { executeAlgorithm } from '../services/algorithmService';

const GRID_SIZE = 20;
const PIXEL_SIZE = 15;

export default function Canvas({ algorithm, onBack, onSelectAlgorithm }) {
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [step, setStep] = useState(-1);
  const [isExecuted, setIsExecuted] = useState(false);
  const [zoom, setZoom] = useState(1.5);
  const [panX, setPanX] = useState(300);
  const [panY, setPanY] = useState(300);
  const [inputs, setInputs] = useState({
    x0: 3,
    y0: 3,
    x1: 17,
    y1: 17,
    centerX: 10,
    centerY: 10,
    radius: 8
  });
  const [isAnimating, setIsAnimating] = useState(false);

  const algorithms = [
    {
      id: 'dda',
      name: 'DDA Line Drawing',
      description: 'Digital Differential Analyzer - Simple incremental algorithm',
      icon: '─',
      ariaLabel: 'DDA Line Drawing algorithm'
    },
    {
      id: 'bresenham',
      name: 'Bresenham Line Drawing',
      description: 'Efficient integer-only line rasterization',
      icon: '╱',
      ariaLabel: 'Bresenham Line Drawing algorithm'
    },
    {
      id: 'midpoint-circle',
      name: 'Midpoint Circle Drawing',
      description: 'Efficient circle rasterization using midpoint concept',
      icon: '●',
      ariaLabel: 'Midpoint Circle Drawing algorithm'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numValue = value === '' || value === '-' ? value : Number.parseInt(value, 10);
    setInputs(prev => ({
      ...prev,
      [name]: numValue
    }));
  };

  const handleExecute = async () => {
    try {
      let payload;
      if (algorithm === 'midpoint-circle') {
        payload = {
          centerX: inputs.centerX,
          centerY: inputs.centerY,
          radius: inputs.radius
        };
      } else {
        payload = {
          x0: inputs.x0,
          y0: inputs.y0,
          x1: inputs.x1,
          y1: inputs.y1
        };
      }

      const result = await executeAlgorithm(algorithm, payload);
      setPoints(result);
      setStep(-1);
      setIsExecuted(true);
      
      // Auto-fit view to show all points
      if (result.length > 0) {
        let minX = result[0].x, maxX = result[0].x;
        let minY = result[0].y, maxY = result[0].y;
        
        result.forEach(point => {
          minX = Math.min(minX, point.x);
          maxX = Math.max(maxX, point.x);
          minY = Math.min(minY, point.y);
          maxY = Math.max(maxY, point.y);
        });
        
        const rangeX = maxX - minX + 2;
        const rangeY = maxY - minY + 2;
        const canvas = canvasRef.current;
        
        if (canvas) {
          const zoomX = (canvas.width * 0.7) / (rangeX * PIXEL_SIZE);
          const zoomY = (canvas.height * 0.7) / (rangeY * PIXEL_SIZE);
          const newZoom = Math.min(zoomX, zoomY, 3);
          
          const scaledPixelSize = PIXEL_SIZE * newZoom;
          const centerX = (minX + maxX) / 2;
          const centerY = (minY + maxY) / 2;
          
          setZoom(newZoom);
          setPanX(canvas.width / 2 - centerX * scaledPixelSize);
          setPanY(canvas.height / 2 + centerY * scaledPixelSize);
        }
      }
    } catch (error) {
      console.error('Error executing algorithm:', error);
    }
  };

  const handlePlayPause = () => {
    if (points.length === 0) return;
    
    if (isAnimating) {
      setIsAnimating(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } else {
      setIsAnimating(true);
      // Reset to beginning if animation finished or hasn't started
      let currentStep = step >= points.length - 1 ? -1 : step;
      
      intervalRef.current = setInterval(() => {
        currentStep++;
        if (currentStep >= points.length - 1) {
          setStep(points.length - 1);
          setIsAnimating(false);
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        } else {
          setStep(currentStep);
        }
      }, 100);
    }
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scaledPixelSize = PIXEL_SIZE * zoom;

    // Save context
    ctx.save();
    ctx.translate(panX, panY);

    // Draw grid
    ctx.strokeStyle = '#f3f4f6';
    ctx.lineWidth = 1;
    
    // Calculate grid bounds
    const minGridX = Math.floor(-panX / scaledPixelSize) - 1;
    const maxGridX = Math.ceil((canvas.width - panX) / scaledPixelSize) + 1;
    const minGridY = Math.floor(-panY / scaledPixelSize) - 1;
    const maxGridY = Math.ceil((canvas.height - panY) / scaledPixelSize) + 1;

    for (let i = minGridX; i <= maxGridX; i++) {
      ctx.beginPath();
      ctx.moveTo(i * scaledPixelSize, minGridY * scaledPixelSize);
      ctx.lineTo(i * scaledPixelSize, maxGridY * scaledPixelSize);
      ctx.stroke();
    }
    for (let i = minGridY; i <= maxGridY; i++) {
      ctx.beginPath();
      ctx.moveTo(minGridX * scaledPixelSize, i * scaledPixelSize);
      ctx.lineTo(maxGridX * scaledPixelSize, i * scaledPixelSize);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    
    // X axis (y = 0)
    ctx.beginPath();
    ctx.moveTo(minGridX * scaledPixelSize, 0);
    ctx.lineTo(maxGridX * scaledPixelSize, 0);
    ctx.stroke();
    
    // Y axis (x = 0)
    ctx.beginPath();
    ctx.moveTo(0, minGridY * scaledPixelSize);
    ctx.lineTo(0, maxGridY * scaledPixelSize);
    ctx.stroke();

    // Draw axis labels
    ctx.fillStyle = '#000000';
    ctx.font = `${Math.max(10, 10 * zoom)}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    for (let i = minGridX; i <= maxGridX; i++) {
      const pos = i * scaledPixelSize;
      ctx.beginPath();
      ctx.moveTo(pos, -3);
      ctx.lineTo(pos, 3);
      ctx.stroke();
      ctx.fillText(String(i), pos, 8);
    }

    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let i = minGridY; i <= maxGridY; i++) {
      const pos = i * scaledPixelSize;
      ctx.beginPath();
      ctx.moveTo(-3, pos);
      ctx.lineTo(3, pos);
      ctx.stroke();
      ctx.fillText(String(-i), -8, pos);
    }

    // Draw real line/circle with 50% opacity
    if (points.length > 0 && isExecuted) {
      if (algorithm === 'midpoint-circle') {
        // Draw real circle
        const centerX = inputs.centerX * scaledPixelSize;
        const centerY = -inputs.centerY * scaledPixelSize;
        const radius = inputs.radius * scaledPixelSize;
        
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Draw real line
        const x0 = inputs.x0 * scaledPixelSize;
        const y0 = -inputs.y0 * scaledPixelSize;
        const x1 = inputs.x1 * scaledPixelSize;
        const y1 = -inputs.y1 * scaledPixelSize;
        
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
        
        // Mark start and end points in dark deep green
        ctx.fillStyle = '#059669';
        const markerRadius = 6;
        
        // Start point marker
        ctx.beginPath();
        ctx.arc(x0, y0, markerRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // End point marker
        ctx.beginPath();
        ctx.arc(x1, y1, markerRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw points
    if (step >= 0) {
      for (let i = 0; i <= step; i++) {
        const point = points[i];
        const x = point.x * scaledPixelSize;
        const y = -point.y * scaledPixelSize;

        if (i === step | i === 0) {
          ctx.fillStyle = '#10b981';
          ctx.beginPath();
          ctx.arc(x, y, 6 * zoom, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = '#01f673';
          ctx.beginPath();
          ctx.arc(x, y, 4 * zoom, 0, Math.PI * 2);
          ctx.fill();
        }

        // Draw coordinates to the right of the point
        ctx.fillStyle = '#000000';
        ctx.font = `${Math.max(9, 9 * zoom)}px Arial`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(`(${point.x},${point.y})`, x + 8, y);
      }
    }

    ctx.restore();
  }, [zoom, panX, panY, step, points, algorithm, inputs, isExecuted]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setIsAnimating(false);
    };
  }, []);

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomFactor = 1.1;
    const newZoom = e.deltaY < 0 ? zoom * zoomFactor : zoom / zoomFactor;
    setZoom(Math.max(0.5, Math.min(3, newZoom)));
  };

  const handleCanvasMouseDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    let lastX = e.clientX - rect.left;
    let lastY = e.clientY - rect.top;

    const handleMouseMove = (moveEvent) => {
      const currentX = moveEvent.clientX - rect.left;
      const currentY = moveEvent.clientY - rect.top;
      const deltaX = currentX - lastX;
      const deltaY = currentY - lastY;
      
      setPanX(prev => prev + deltaX);
      setPanY(prev => prev + deltaY);
      
      lastX = currentX;
      lastY = currentY;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleResetView = () => {
    if (points.length > 0) {
      let minX = points[0].x, maxX = points[0].x;
      let minY = points[0].y, maxY = points[0].y;
      
      points.forEach(point => {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      });
      
      const rangeX = maxX - minX + 2;
      const rangeY = maxY - minY + 2;
      const canvas = canvasRef.current;
      
      if (canvas) {
        const zoomX = (canvas.width * 0.7) / (rangeX * PIXEL_SIZE);
        const zoomY = (canvas.height * 0.7) / (rangeY * PIXEL_SIZE);
        const newZoom = Math.min(zoomX, zoomY, 3);
        
        const scaledPixelSize = PIXEL_SIZE * newZoom;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        
        setZoom(newZoom);
        setPanX(canvas.width / 2 - centerX * scaledPixelSize);
        setPanY(canvas.height / 2 + centerY * scaledPixelSize);
      }
    } else {
      setZoom(1);
      setPanX(0);
      setPanY(0);
    }
  };

  const inputFields = algorithm === 'midpoint-circle'
    ? [
        { name: 'centerX', label: 'Center X', min: -GRID_SIZE, max: GRID_SIZE },
        { name: 'centerY', label: 'Center Y', min: -GRID_SIZE, max: GRID_SIZE },
        { name: 'radius', label: 'Radius', min: 1, max: 150 }
      ]
    : [
        { name: 'x0', label: 'Start X', min: -GRID_SIZE, max: GRID_SIZE },
        { name: 'y0', label: 'Start Y', min: -GRID_SIZE, max: GRID_SIZE },
        { name: 'x1', label: 'End X', min: -GRID_SIZE, max: GRID_SIZE },
        { name: 'y1', label: 'End Y', min: -GRID_SIZE, max: GRID_SIZE }
      ];

  return (
    <div className="canvas-container">
      <div className="canvas-content">
        <div className="canvas-layout">
          <div className="visualization">
            <canvas
              ref={canvasRef}
              width={600}
              height={600}
              className="drawing-canvas"
              onWheel={handleWheel}
              onMouseDown={handleCanvasMouseDown}
            />
            <div className="canvas-controls">
              <button className="zoom-btn" onClick={() => setZoom(prev => Math.min(3, prev * 1.2))} title="Zoom In">
                🔍+
              </button>
              <button className="zoom-btn" onClick={() => setZoom(prev => Math.max(0.5, prev / 1.2))} title="Zoom Out">
                🔍−
              </button>
              <button className="zoom-btn" onClick={handleResetView} title="Reset View">
                ⟲
              </button>
              <span className="zoom-level">{(zoom * 100).toFixed(0)}%</span>
            </div>
          </div>

          <div className="right-panel">
            <div className="control-panel">
              <div className="algorithm-switcher">
                <h3>Algorithms</h3>
                <div className="algorithm-cards">
                  {algorithms.map((algo) => (
                    <button
                      key={algo.id}
                      className={`algo-card ${algorithm === algo.id ? 'active' : ''}`}
                      onClick={() => {
                        onSelectAlgorithm(algo.id);
                        setPoints([]);
                        setStep(-1);
                        setIsExecuted(false);
                        setZoom(1.5);
                        setPanX(300);
                        setPanY(300);
                      }}
                      aria-label={algo.ariaLabel}
                    >
                      <h4>{algo.name}</h4>
                    </button>
                  ))}
                </div>
              </div>

              <div className="inputs-section">
                <h3>Parameters</h3>
                <div className="input-group">
                  {inputFields.map(field => (
                    <div key={field.name} className="input-field">
                      <label>{field.label}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        name={field.name}
                        value={inputs[field.name] || ''}
                        onChange={handleInputChange}
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="controls-section">
                <div className="execute-reset-group">
                  <button className="primary-btn" onClick={handleExecute}>
                    Execute Algorithm
                  </button>
                  <button 
                    className="control-btn"
                    onClick={() => {
                      setPoints([]);
                      setStep(-1);
                      setIsExecuted(false);
                      setZoom(1.5);
                      setPanX(300);
                      setPanY(300);
                    }}
                  >
                    ⟲ Reset
                  </button>
                </div>
                
                {points.length > 0 && (
                  <>
                    <div className="animation-controls">
                      <button 
                        className="control-btn"
                        onClick={handlePlayPause}
                      >
                        {isAnimating ? '⏸ Pause' : '▶ Play'}
                      </button>
                      <button 
                        className="control-btn"
                        onClick={() => step > 0 && setStep(step - 1)}
                      >
                        ← Previous
                      </button>
                      <button 
                        className="control-btn"
                        onClick={() => step < points.length - 1 && setStep(step + 1)}
                      >
                        Next →
                      </button>
                    </div>

                    <div className="info-box">
                      <p><strong>Total Points:</strong> {points.length}</p>
                      <p><strong>Current Step:</strong> {step + 1} / {points.length}</p>
                      {step >= 0 && (
                        <>
                          <p><strong>Position:</strong> ({points[step].x}, {points[step].y})</p>
                          <p><strong>Decision Parameter:</strong> {points[step].decisionParameter}</p>
                          <p><strong>Decision:</strong> {points[step].decision}</p>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

Canvas.propTypes = {
  algorithm: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onSelectAlgorithm: PropTypes.func.isRequired
};
